import type { FC } from "react";

import classNames from "classnames";
import dompurify from "isomorphic-dompurify";

import { useConfig } from "@jti/app-context/useConfig";
import type { HistoryPointsItemType, HistoryReferralItem } from "@jti/store/slices/loyaltyCoreSlice";
import { Table, type TableColumn } from "@jti/ui/components/Table";
import { useTranslations } from "@jti/ui/providers/useTranslations";
import { formatDate } from "@jti/ui/utils/formatDate";

import { MobileHistoryCards } from "./MobileHistoryCards/MobileHistoryCards";
import getEventDisplayName from "./util/getEventDisplayName";

import styles from "./HistoryTable.module.scss";

type HistoryRowItem = HistoryPointsItemType | HistoryReferralItem;

export type LabelsType = {
  points: string;
  event: string;
  balance?: string;
  date: string;
};

export type HistoryTableProps = {
  rows: HistoryRowItem[];
  variant?: "referral" | "points";
  labels: LabelsType;
  pointsUnit?: string;
  eventMessages?: EventMessagesType;
  isCouponDisplayedInNewLineOnMobile?: boolean;
};

export type EventMessagesType = {
  defaultPointsAddedMessage: string;
  defaultPointsSpentMessage: string;
  pointsRefundMessage?: string;
  pointsOrderAddedMessage: string;
  pointsRedemptionMessage: string;
  pointsCouponRedemptionMessage: string;
  pointsCancellationMessage: string;
  pointsExpirationMessage: string;
  externalCouponLinkLabel?: string;
  couponLabel?: string;
  pinLabel?: string;
  redemptionFailedMessage?: string;
};

const { sanitize } = dompurify;

export const HistoryTable: FC<HistoryTableProps> = ({
  rows,
  labels,
  variant,
  pointsUnit,
  eventMessages,
  isCouponDisplayedInNewLineOnMobile = false,
}) => {
  const isReferral = variant === "referral";
  const { __ } = useTranslations();
  const { PROFILE_PAGE_URL } = useConfig();

  const transformedRows = rows.map((singleElement) => {
    const isBalanceAvailable = "balance" in singleElement;
    const status =
      "is_added" in singleElement && typeof singleElement.is_added === "boolean"
        ? singleElement.is_added
        : isBalanceAvailable
          ? singleElement.type === "PointsWereAdded" || singleElement.type === "PointsWereRefunded"
          : true;
    const points = `${singleElement.points} ${pointsUnit ?? ""}`;
    const pointsWithSign = !status && !isReferral ? `-${points}` : points;
    const eventKey =
      isReferral &&
      (singleElement.event.startsWith("referrer_network_") || singleElement.event === "referrers_affiliate_registered")
        ? `loyalty.earning_rule.${singleElement.event}`
        : singleElement.event;
    const event =
      "type" in singleElement && !isReferral
        ? getEventDisplayName(__, singleElement, eventMessages, isCouponDisplayedInNewLineOnMobile, PROFILE_PAGE_URL)
        : __(eventKey);

    return {
      ...singleElement,
      status,
      isReferral,
      event,
      points: pointsWithSign,
      balance: isBalanceAvailable ? singleElement.balance : undefined,
      date: formatDate(singleElement.date),
    };
  });

  const columns: TableColumn[] = [
    {
      key: "date",
      label: labels.date,
      className: styles.contentWidthColumn,
    },
    {
      key: "event",
      label: labels.event,
      className: styles.fullWidthColumn,
      render: (value) => (
        <div
          className={styles.event}
          dangerouslySetInnerHTML={{ __html: sanitize(String(value ?? "")) }}
          data-testid="historyTableEvent"
        />
      ),
    },
    {
      key: "points",
      label: labels.points,
      className: styles.contentWidthColumn,
      render: (value, row) => (
        <div className={classNames(styles.status, { [styles.lastColumn]: isReferral })}>
          <div
            className={classNames(styles.bullet, {
              [styles.success]: row.status,
            })}
          />
          <span className={styles.points}>{value}</span>
        </div>
      ),
    },
    ...(isReferral
      ? []
      : [
          {
            key: "balance",
            label: labels.balance,
            className: styles.contentWidthColumn,
          },
        ]),
  ];

  return (
    <>
      <Table testId="history-table" columns={columns} rows={transformedRows} className={styles.historyTable} />
      <MobileHistoryCards isReferral={isReferral} rows={transformedRows} labels={labels} />
    </>
  );
};
